import React from 'react';
import { NavLink } from 'react-router-dom';
import { Triangle, SquaresFour, Clock, ArrowRight } from '@phosphor-icons/react';

/**
 * As-Is summary strip for the Overview.
 *
 * The Overview is the As-Is photo (Part 0 is the protagonist). This strip gives
 * the headline result figures a place on the front page without duplicating the
 * detail: each tile states one number and links to the module that owns it, so
 * the Overview routes rather than re-analyses.
 */
export interface AsIsSummaryStripProps {
  part3: any;   // controlling_capacity, limiting_nodes
  apcas: any;   // rac_capacity, nss_pct
  safeNum: (v: any, decimals?: number) => string;
  language: 'es' | 'en';
}

const NODE_LABEL: Record<string, { es: string; en: string; token: string }> = {
  rrc: { es: 'Pista', en: 'Runway', token: 'var(--node-rrc)' },
  rac: { es: 'Plataforma', en: 'Apron', token: 'var(--node-rac)' },
  ptbc: { es: 'Terminal', en: 'Terminal', token: 'var(--node-ptbc)' },
};

const AsIsSummaryStrip: React.FC<AsIsSummaryStripProps> = ({ part3, apcas, safeNum, language }) => {
  const es = language === 'es';

  const cc = part3?.controlling_capacity;
  const limiting: string[] = Array.isArray(part3?.limiting_nodes) ? part3.limiting_nodes : [];
  // The limiting node's colour tints the CC tile. With a tie we do not pick one
  // (that would invent precision); the tile stays neutral and names them all.
  const limitTint = limiting.length === 1 ? NODE_LABEL[limiting[0]]?.token : undefined;
  const limitNames = limiting.map((n) => (es ? NODE_LABEL[n]?.es : NODE_LABEL[n]?.en) || n).join(' · ');

  const nssPct = apcas?.nss_pct;
  const nssNum = typeof nssPct === 'number' ? nssPct : parseFloat(safeNum(nssPct, 0));
  const nssToken =
    nssNum >= 95 ? 'var(--aipron-danger)' :
    nssNum >= 80 ? 'var(--aipron-warning)' :
    nssNum >= 60 ? 'var(--aipron-warning)' : 'var(--aipron-success)';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {/* Controlling capacity -> Equilibrium */}
      <NavLink
        to="../equilibrium"
        className="pro-card p-5 group"
        style={{ borderColor: limitTint ? `color-mix(in srgb, ${limitTint} 40%, var(--border))` : undefined }}
      >
        <div className="flex items-center justify-between">
          <span className="eyebrow flex items-center gap-1.5"><Triangle size={13} weight="fill" /> {es ? 'Capacidad controladora' : 'Controlling capacity'}</span>
          <ArrowRight size={14} className="opacity-0 group-hover:opacity-60 transition-opacity" />
        </div>
        <p className="kpi-value mt-2">{safeNum(cc, 1)} <span className="text-base font-normal text-[var(--text-tertiary)]">mov/h</span></p>
        <p className="text-xs mt-1" style={{ color: limitTint || 'var(--text-secondary)' }}>
          {limiting.length === 0
            ? (es ? 'Sin nodo limitante configurado' : 'No limiting node configured')
            : `${es ? 'Limita' : 'Limited by'}: ${limitNames}`}
        </p>
      </NavLink>

      {/* RAC -> AP-CAM */}
      <NavLink to="../apcas" className="pro-card p-5 group" style={{ borderColor: 'color-mix(in srgb, var(--node-rac) 40%, var(--border))' }}>
        <div className="flex items-center justify-between">
          <span className="eyebrow flex items-center gap-1.5"><SquaresFour size={13} weight="fill" /> {es ? 'Capacidad de plataforma' : 'Apron capacity'}</span>
          <ArrowRight size={14} className="opacity-0 group-hover:opacity-60 transition-opacity" />
        </div>
        <p className="kpi-value mt-2" style={{ color: 'var(--node-rac)' }}>{safeNum(apcas?.rac_capacity, 1)} <span className="text-base font-normal text-[var(--text-tertiary)]">mov/h</span></p>
        <p className="text-xs mt-1 text-[var(--text-secondary)]">RAC · {es ? 'ver AP-CAM' : 'see AP-CAM'}</p>
      </NavLink>

      {/* NSS -> AP-CAM */}
      <NavLink to="../apcas" className="pro-card p-5 group" style={{ borderColor: `color-mix(in srgb, ${nssToken} 40%, var(--border))` }}>
        <div className="flex items-center justify-between">
          <span className="eyebrow flex items-center gap-1.5"><Clock size={13} weight="fill" /> {es ? 'Saturación (NSS)' : 'Saturation (NSS)'}</span>
          <ArrowRight size={14} className="opacity-0 group-hover:opacity-60 transition-opacity" />
        </div>
        <p className="kpi-value mt-2" style={{ color: nssToken }}>{safeNum(nssPct, 0)}<span className="text-base font-normal text-[var(--text-tertiary)]">%</span></p>
        <p className="text-xs mt-1 text-[var(--text-secondary)]">
          {nssNum >= 95 ? (es ? 'Saturado' : 'Saturated') : nssNum >= 80 ? (es ? 'Crítico' : 'Critical') : nssNum >= 60 ? (es ? 'Elevado' : 'Elevated') : (es ? 'Normal' : 'Normal')}
        </p>
      </NavLink>
    </div>
  );
};

export default AsIsSummaryStrip;
